import React, { useEffect, useState } from "react";
import { createStructuredSelector } from "reselect";
import { connect, useSelector } from "react-redux";
import { isEmpty } from "lodash";
import classNames from "classnames";

import Field from "components/Field";
import Input from "components/Input";
import Checkbox from "components/Input/Checkbox";
import Select from "components/Input/Select";
import InputRow from "components/Input/Row";
import InputWithUnits from "components/Input/InputWithUnits";
import ZoneRoomTable from "./ZoneRoomTable";
import ZoneWarnings from "./ZoneWarnings";
import ZoneErrors from "./ZoneErrors";

import { useStateSelector } from "utils/field";
import { getDecimalPlaces, getValidation, required, validateField } from "utils/fieldValidation";
import {
    generateZoneLabel,
    calculateMinZoneArea,
    calculateMinZonePerimeter,
    getCatOptions,
    getComponentsInStorey,
    getRoomsInStorey,
    getFieldLabel,
    getFieldInfo,
    VENTILATION_OPTIONS,
    makeZoneLabelUnique,
    calculateSczAirFlowRate,
} from "utils/singleZoneCooling";
import { getMeasurementUnits } from "utils/displayMeasurements";
import { getBaseUnits, getUnitOptions } from "utils/fields";
import { initForm, resetForm } from "store/form/actions";
import { selectUpgradePackagesSortedArray } from "features/Model/Upgrades/_ducks/selectors";

import classes from "./styles.module.scss";

const airFlowValidation = getValidation("hrvSupply");
const airFlowDecimals = getDecimalPlaces("ventSupply");
const airFlowUnitOptions = getUnitOptions("airFlow");

const ZoneForm = ({
    zoneId = "",
    accessor = "newForm.coolingZoneDrawer",
    change,
    displayUnits = "Metric",
    hasErrors = false,
    inZoneAccordion = false,
}) => {
    const {
        singleZoneCooling: { occupants: coolingZoneOccupants = 0, homeHasCentralAirSystem = false } = {},
        roomSummary = {},
        components = {},
        intComponents = {},
        coolingZones = {},
        specifications: { numStoreys = {} } = {},
    } = useStateSelector(`model.modelData`) || {};

    const {
        label: zoneLabel = "",
        autoGenerateLabel = false,
        selectedStorey = null,
        selectedRooms = [],
        selectedWalls = [],
        isFullyEnclosed = false,
        measurements: {
            totalFloorArea: zoneArea = 0,
            totalWallPerimeter: zonePerimeter = 0,
        } = {},
        requirements: {
            hasMinArea = false,
            hasWallOutlet = false,
            hasOtherElectrical = false,
            hasVentilation = false,
            ventilationType = null,
        } = {},
        warnings = {
            missingInteriorDoor: false,
            notFullyEnclosed: false,
            roomsWithIntWalls: [],
        },
        systemServesZone = false,
        designAirHandlingCirculationRate = 0,
        upgradePackageId = "",
    } = useStateSelector(`${accessor}`) || {};

    // Upgrade Packages
    const upgradePackages = useSelector(selectUpgradePackagesSortedArray) || [];
    const upgradePackageOptions = [
        { value: "", label: "Base" },
        ...upgradePackages.map(({ id, name }) => ({ value: id, label: name || id })),
    ];

    // Zone Storey Location
    const { id: storeysId } = numStoreys;
    const catOptions = getCatOptions(storeysId);
    const roomsInStorey = getRoomsInStorey(roomSummary, selectedStorey);
    const { componentsInStorey, intComponentsInStorey } = getComponentsInStorey(components, intComponents, selectedStorey);

    // Zone Label
    const labelValidation = autoGenerateLabel ? [] : [required];
    const { errors, invalid } = validateField("", zoneLabel, labelValidation);

    useEffect(() => {
        if (autoGenerateLabel) {
            const generatedLabel = generateZoneLabel(roomSummary, storeysId, selectedStorey, selectedRooms);
            const uniqueLabel = makeZoneLabelUnique(coolingZones, generatedLabel, zoneId);

            change(`${accessor}.label`, uniqueLabel);
        }
    }, [autoGenerateLabel, selectedStorey, selectedRooms]);

    // Zone Measurements
    const { areaDisplayUnits = "m2", perimeterDisplayUnits = "m" } = getMeasurementUnits(displayUnits);

    const minAreaRequired = calculateMinZoneArea(coolingZoneOccupants);
    const minPerimeterRequired = calculateMinZonePerimeter(zoneArea);

    // Update the error for the minimum area
    useEffect(() => {
        change(`${accessor}.requirements.hasMinArea`, zoneArea >= minAreaRequired);
    }, [zoneArea, coolingZoneOccupants]);

    // Design Ventilation Air Flow Rate Input
    const airFlowBaseUnits = getBaseUnits("hrvSupply", displayUnits);
    const { trueBase: airFlowTrueBase, displayBase: airFlowDisplayBase } = airFlowBaseUnits;

    const [sczVentilationAirFlowRate, setSczVentilationAirFlowRate] = useState(0);
    const [airFlowUnits, setAirFlowUnits] = useState(airFlowTrueBase);

    const airFlowU = airFlowUnits || airFlowDisplayBase || airFlowTrueBase;

    useEffect(() => {
        const newSczVentilationAirFlowRate = calculateSczAirFlowRate(
            coolingZoneOccupants,
            homeHasCentralAirSystem,
            systemServesZone,
            designAirHandlingCirculationRate,
            zoneArea,
        );
        setSczVentilationAirFlowRate(newSczVentilationAirFlowRate);
    }, [designAirHandlingCirculationRate, homeHasCentralAirSystem, systemServesZone, coolingZoneOccupants, zoneArea]);

    return (
        <div>
            {/* ZONE DETAILS INPUT FIELDS */}
            <InputRow gridTemplate="1fr 1fr">
                <Field
                    component={Input}
                    name={`${accessor}.label`}
                    label="Cooling Zone Label"
                    placeholder="Enter label"
                    validate={labelValidation}
                    disabled={autoGenerateLabel}
                    meta={{
                        invalid: invalid,
                        error: errors,
                    }}
                />
                <Field
                    component={Checkbox}
                    name={`${accessor}.autoGenerateLabel`}
                    label="Use automatically generated label"
                />
                <Field
                    component={Select}
                    name={`${accessor}.selectedStorey`}
                    label="Storey"
                    options={catOptions}
                />
                <Field
                    component={Select}
                    name={`${accessor}.upgradePackageId`}
                    label="Upgrade Package"
                    placeholder="Base"
                    options={upgradePackageOptions}
                    info="Applied to this cooling zone when the analysis runs. Base uses the model as it is."
                />
            </InputRow>

            <ZoneRoomTable
                change={change}
                accessor={`${accessor}`}
                roomsInStorey={roomsInStorey}
                componentsInStorey={componentsInStorey}
                intComponentsInStorey={intComponentsInStorey}
                numOfOccupants={coolingZoneOccupants}
                minPerimeterRequired={minPerimeterRequired}
                minAreaRequired={minAreaRequired}
                displayUnits={{
                    areaDisplayUnits: areaDisplayUnits,
                    perimeterDisplayUnits: perimeterDisplayUnits,
                }}
                inZoneAccordion={inZoneAccordion}
            />

            {/* Recommended Fields */}
            <h3 className={classes.sectionTitle}>Guideline on Single Zone Cooling in Dwelling Units Checklist</h3>
            <div className={classNames(classes.checklistContainer, { [classes.hasWarning]: !isEmpty(warnings) })}>
                <Field
                    component={Checkbox}
                    className={classes.checkboxContainer}
                    name={`${accessor}.isFullyEnclosed`}
                    label={getFieldLabel("isFullyEnclosed")}
                    info={getFieldInfo("isFullyEnclosed", isFullyEnclosed)}
                    disabled={true}
                />
                <ZoneWarnings
                    minPerimeterRequired={minPerimeterRequired}
                    warnings={warnings}
                    displayUnits={{
                        areaDisplayUnits,
                        perimeterDisplayUnits,
                    }}
                />
            </div>

            {/* Required Fields */}
            <div className={classNames(classes.checklistContainer, { [classes.hasError]: selectedStorey && hasErrors })}>
                <Field
                    component={Checkbox}
                    className={classes.checkboxContainer}
                    name={`${accessor}.requirements.hasMinArea`}
                    label={getFieldLabel("hasMinArea")}
                    disabled={true}
                    info={getFieldInfo("hasMinArea", hasMinArea)}
                />
                <Field
                    component={Checkbox}
                    className={classes.checkboxContainer}
                    name={`${accessor}.requirements.hasWallOutlet`}
                    label={getFieldLabel("hasWallOutlet")}
                />
                <Field
                    component={Checkbox}
                    className={classes.checkboxContainer}
                    name={`${accessor}.requirements.hasOtherElectrical`}
                    label={getFieldLabel("hasOtherElectrical")}
                />
                <div className={classes.ventilationContainer}>
                    <Field
                        component={Checkbox}
                        className={classes.checkboxContainer}
                        name={`${accessor}.requirements.hasVentilation`}
                        label={getFieldLabel("hasVentilation")}
                        onChange={(value) => {
                            change(`${accessor}.requirements.hasVentilation`, value);

                            if (!value) {
                                change(`${accessor}.requirements.ventilationType`, null);
                            }
                        }}
                    />
                    <Field
                        component={Select}
                        name={`${accessor}.requirements.ventilationType`}
                        hideLabel={true}
                        options={VENTILATION_OPTIONS}
                        onChange={(value) => {
                            change(`${accessor}.requirements.ventilationType`, value);

                            if (!hasVentilation) {
                                change(`${accessor}.requirements.hasVentilation`, true);
                            }
                        }}
                        meta={{
                            invalid: hasVentilation && ventilationType === null,
                            error: "Required",
                        }}
                    />
                </div>
                {selectedStorey && (
                    <ZoneErrors
                        hasMinArea={hasMinArea}
                        hasErrors={hasErrors}
                        numOfOccupants={coolingZoneOccupants}
                        floorArea={zoneArea}
                        displayUnits={{
                            areaDisplayUnits,
                            perimeterDisplayUnits,
                        }}
                    />
                )}
            </div>
            <div className={classes.checklistContainer}>
                <Checkbox
                    className={classes.checkboxContainer}
                    label={getFieldLabel("homeHasCentralAirSystem")}
                    input={{
                        value: homeHasCentralAirSystem,
                    }}
                    info={getFieldInfo("homeHasCentralAirSystem", homeHasCentralAirSystem)}
                    disabled
                />
                <Field
                    component={Checkbox}
                    className={classes.checkboxContainer}
                    name={`${accessor}.systemServesZone`}
                    label={getFieldLabel("systemServesZone")}
                    disabled={!homeHasCentralAirSystem}
                />

                <InputRow gridTemplate="1fr 1fr" lastRow>
                    <Field
                        component={InputWithUnits}
                        label="Design Air Handling Circulation Rate"
                        type="number"
                        name={`${accessor}.designAirHandlingCirculationRate`}
                        setValue={designAirHandlingCirculationRate}
                        validate={airFlowValidation}
                        decimals={airFlowDecimals}
                        units={{
                            base: airFlowBaseUnits,
                            options: airFlowUnitOptions,
                            selected: airFlowU,
                            unitType: "airFlow",
                        }}
                        onUnitChange={(unit) => setAirFlowUnits(unit)}
                        disabled={!(homeHasCentralAirSystem && systemServesZone)}
                    />
                    <InputWithUnits
                        label="Applied Ventilation Air Flow Rate"
                        type="number"
                        input={{
                            value: sczVentilationAirFlowRate,
                            onChange: (value) => setSczVentilationAirFlowRate(value),
                        }}
                        setValue={sczVentilationAirFlowRate}
                        validate={airFlowValidation}
                        decimals={airFlowDecimals}
                        units={{
                            base: airFlowBaseUnits,
                            options: airFlowUnitOptions,
                            selected: airFlowU,
                            unitType: "airFlow",
                        }}
                        onUnitChange={(unit) => setAirFlowUnits(unit)}
                        disabled
                    />
                </InputRow>
            </div>
        </div>
    )
};

const mapStateToProps = createStructuredSelector({});

const mapDispatchToProps = (dispatch) => ({
    resetForm: (formId) => dispatch(resetForm(formId)),
    initForm: (formId, values) => dispatch(initForm(formId, values)),
});

export default connect(mapStateToProps, mapDispatchToProps)(ZoneForm);